var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { Fragment, useContext, useEffect, useMemo, useState, } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import Collapse from '@material-ui/core/Collapse';
import Paper from '@material-ui/core/Paper';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import { ExactaTableContextProvider } from '../../context/ExactaTableContext';
import ProcessSearchContext from '../../context/process-search-context';
import { paginationSearchWithDefaultQuery } from '../../helpers/search';
import { useProcessList } from '../../hooks';
import { searchProcessData_AC } from '../../store/actions/process';
import { processDataCountSelector } from '../../store/selectors/process';
import StyledIcon from '../CustomStyledIcon';
import PossibleMovementsMenu from '../PossibleMovementsMenu';
import ExactaTableHead from './ExactaTableHead';
import TableToolbar from './ExactaTableToolbar';
var useStyles = makeStyles(function (theme) {
    return createStyles({
        root: {
            width: '100%',
        },
        paper: {
            width: '100%',
            marginBottom: theme.spacing(2),
        },
        table: {
            minWidth: 750,
        },
        visuallyHidden: {
            border: 0,
            clip: 'rect(0 0 0 0)',
            height: 1,
            margin: -1,
            overflow: 'hidden',
            padding: 0,
            position: 'absolute',
            top: 20,
            width: 1,
        },
    });
});
var ExactaTable = function (_a) {
    var title = _a.title, columnDefs = _a.columnDefs, rowsPerPageOptions = _a.rowsPerPageOptions, options = _a.options, actions = _a.actions, modelAlias = _a.modelAlias, expandableRow = _a.expandableRow, _b = _a.selectedActions, selectedActions = _b === void 0 ? [] : _b, _c = _a.size, size = _c === void 0 ? 'small' : _c, _d = _a.apiVersion, apiVersion = _d === void 0 ? 1 : _d, valueExtractor = _a.valueExtractor;
    var _e, _f, _g, _h;
    var classes = useStyles({});
    var dispatch = useDispatch();
    var totalRows = useSelector(processDataCountSelector(modelAlias));
    var _j = useState(false), selectAll = _j[0], setSelectAll = _j[1];
    var _k = useState({
        sortAlias: (_f = (_e = options) === null || _e === void 0 ? void 0 : _e.sorting) === null || _f === void 0 ? void 0 : _f.defaultSortingAlias,
        sortOrder: (_h = (_g = options) === null || _g === void 0 ? void 0 : _g.sorting) === null || _h === void 0 ? void 0 : _h.defaultSortOrder,
    }), sorting = _k[0], setSorting = _k[1];
    var _l = useState({}), filters = _l[0], setFilters = _l[1];
    var _m = useState({}), additionalFilters = _m[0], setAdditionalFilters = _m[1];
    var _o = useState(-1), activeRow = _o[0], setActiveRow = _o[1];
    var _p = useState(columnDefs), columns = _p[0], setColumns = _p[1];
    var _q = useState([]), selected = _q[0], setSelected = _q[1];
    var _r = useState(0), page = _r[0], setPage = _r[1];
    var _s = useState(rowsPerPageOptions[0]), rowsPerPage = _s[0], setRowsPerPage = _s[1];
    var onSortClick = function (alias, order) {
        setSorting({ sortAlias: alias, sortOrder: order });
    };
    var onApplyAdditionalFilters = function (additionalFilters) {
        setPage(0);
        setAdditionalFilters(additionalFilters);
    };
    var psContext = useContext(ProcessSearchContext);
    var filterQuery = useMemo(function () {
        var combinedFilters = __assign(__assign({}, filters), additionalFilters);
        return Object.keys(combinedFilters).map(function (key) {
            var selector = key;
            var selectorValue = combinedFilters[key];
            var comparison = 'contains';
            if (typeof selectorValue === 'string') {
                return {
                    selector: selector,
                    comparison: comparison,
                    value: selectorValue,
                };
            }
            if (typeof selectorValue === 'boolean') {
                comparison = '=';
                if (selectorValue === false) {
                    comparison = 'not';
                }
                return {
                    selector: selector,
                    comparison: comparison,
                    value: "" + selectorValue,
                };
            }
            if (typeof selectorValue === 'object') {
                return {
                    selector: selector,
                    value: selectorValue.id,
                };
            }
            return [];
        });
    }, [filters, additionalFilters]);
    var rows = useProcessList(modelAlias);
    var columnsToShow = useMemo(function () { return columns.filter(function (column) { return !column.hidden; }); }, [columns]);
    useEffect(function () {
        dispatch(searchProcessData_AC(modelAlias, paginationSearchWithDefaultQuery({
            modelAlias: modelAlias,
            filters: filterQuery,
            perPage: rowsPerPage,
            currentPage: page + 1,
            defaultQuery: options.defaultQuery,
            orderingAlias: sorting.sortAlias,
            order: sorting.sortOrder,
        }), psContext.name, options.selection || false));
    }, [
        dispatch,
        filterQuery,
        psContext.name,
        modelAlias,
        rowsPerPage,
        page,
        sorting,
    ]);
    var onApplyFilter = function (filter) {
        setPage(0);
        setFilters(function (filters) {
            return __assign(__assign({}, filters), filter);
        });
    };
    var handleDeleteFilter = function (filter) {
        if (additionalFilters[filter]) {
            setAdditionalFilters(function (prevValue) {
                delete prevValue[filter];
                return __assign({}, prevValue);
            });
        }
        else {
            setFilters(function (filters) {
                delete filters[filter];
                return __assign({}, filters);
            });
        }
    };
    var handleSelectAllClick = function (event) {
        if (event.target.checked) {
            var newSelecteds = rows.map(function (n) { return n.id; });
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };
    var handleClick = function (event, id) {
        if (!event.target.checked) {
            setSelectAll(false);
        }
        var selectedIndex = selected.indexOf(id);
        var newSelected = [];
        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        }
        else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        }
        else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        }
        else if (selectedIndex > 0) {
            newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
        }
        setSelected(newSelected);
    };
    var handleChangePage = function (event, newPage) {
        setPage(newPage);
    };
    var handleChangeRowsPerPage = function (event) {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    var handleViewColumnChange = function (viewChangedColumn) {
        setColumns(function (prevColumns) {
            return prevColumns.map(function (column) {
                if (viewChangedColumn.alias === column.alias) {
                    return __assign(__assign({}, column), { hidden: !column.hidden });
                }
                return column;
            });
        });
    };
    var markAllSelected = function () {
        var newSelecteds = rows.map(function (n) { return n.id; });
        setSelected(newSelecteds);
    };
    var handleSelectAll = function () {
        markAllSelected();
        setSelectAll(true);
    };
    var handleClearSelection = function () {
        setSelectAll(false);
        setSelected([]);
    };
    var isRowSelected = function (name) { return selected.indexOf(name) !== -1; };
    var selection = options.selection, showPossibleMovements = options.showPossibleMovements;
    var t = useTranslation().t;
    return (React.createElement("div", { className: classes.root },
        React.createElement(ExactaTableContextProvider, { value: {
                columns: columns,
                modelAlias: modelAlias,
                filters: filters,
                additionalFilters: additionalFilters,
                options: options,
                filterQuery: filterQuery,
                totalRows: totalRows,
                rowsPerPage: rowsPerPage,
                selectAll: selectAll,
                handleSelectAll: handleSelectAll,
                handleClearSelection: handleClearSelection,
                markAllSelected: markAllSelected,
            } },
            React.createElement(Paper, { className: classes.paper },
                React.createElement(TableToolbar, { selected: selected, selectedActions: selectedActions, numSelected: selected.length, title: t(title), handleViewColumnChange: handleViewColumnChange, actions: actions, filters: __assign(__assign({}, filters), additionalFilters), onDeleteFilter: handleDeleteFilter, additionalFilters: options.additionalFilters, onApplyAdditionalFilters: onApplyAdditionalFilters }),
                React.createElement(TableContainer, null,
                    React.createElement(Table, { className: classes.table, "aria-labelledby": t(title), size: size, "aria-label": "exacta-table-" + modelAlias },
                        React.createElement(ExactaTableHead, { numSelected: selected.length, onSelectAllClick: handleSelectAllClick, rowCount: rows.length, columns: columnsToShow, options: options, onApplyFilter: onApplyFilter, appliedFilters: filters, expandableRow: expandableRow, sorting: sorting, onSortClick: onSortClick }),
                        React.createElement(TableBody, null, rows.map(function (row, index) {
                            var isItemSelected = isRowSelected(row.id);
                            var labelId = "exacta-table-" + modelAlias + "-" + index;
                            var open = activeRow === index;
                            return (React.createElement(Fragment, { key: row.id },
                                React.createElement(TableRow, { hover: true, "aria-checked": isItemSelected, tabIndex: -1, key: row.id, selected: isItemSelected },
                                    selection && (React.createElement(TableCell, { padding: "checkbox" },
                                        React.createElement(Checkbox, { onClick: function (event) { return handleClick(event, row.id); }, checked: isItemSelected, inputProps: { 'aria-labelledby': labelId } }))),
                                    expandableRow && (React.createElement(TableCell, { padding: "checkbox", onClick: function () { return setActiveRow(open ? -1 : index); } }, open ? (React.createElement(StyledIcon, null, "keyboard_arrow_down")) : (React.createElement(StyledIcon, null, "keyboard_arrow_right")))),
                                    columnsToShow.map(function (column, colIndex) {
                                        var cellValue = valueExtractor(row, column.alias);
                                        return (React.createElement(TableCell, { key: "row-" + index + "-column-" + colIndex, component: "th", id: labelId, padding: selection && colIndex === 0
                                                ? 'none'
                                                : 'default', align: column.align }, column.customRender
                                            ? column.customRender(cellValue, row)
                                            : cellValue));
                                    }),
                                    showPossibleMovements && (React.createElement(TableCell, { padding: "checkbox", align: "right" },
                                        React.createElement(PossibleMovementsMenu, { processObject: row })))),
                                expandableRow && (React.createElement(TableRow, null,
                                    React.createElement(TableCell, { style: { paddingBottom: 0, paddingTop: 0 }, colSpan: columnDefs.length + 3 },
                                        React.createElement(Collapse, { in: open, timeout: "auto", unmountOnExit: true },
                                            React.createElement(Box, { marginTop: 2, marginBottom: 4 }, expandableRow(row))))))));
                        })))),
                React.createElement(TablePagination, { rowsPerPageOptions: rowsPerPageOptions, component: "div", count: totalRows, rowsPerPage: rowsPerPage, page: page, onChangePage: handleChangePage, onChangeRowsPerPage: handleChangeRowsPerPage })))));
};
export default ExactaTable;
//# sourceMappingURL=index.js.map